/**
 * Custom set filter for AG Grid Community edition (the built-in `agSetColumnFilter` is
 * Enterprise-only). Renders a list of checkboxes for each unique value in the column.
 * Receives pre-computed values via filterParams.values.
 *
 * Copied verbatim from the sibling wltp-findings/overview app's src/SetFilter.js (which already
 * solves this same Community-edition gap) - see ui/CONTEXT.md.
 */
export class SetFilter {
  init(params) {
    this.params = params
    this.selectedValues = new Set()
    this.allValues = []

    // Use pre-computed values from filterParams if available
    if (params.values && Array.isArray(params.values)) {
      this.allValues = params.values
    }

    // Select all by default
    this.allValues.forEach(v => this.selectedValues.add(v))

    this.gui = document.createElement('div')
    this.gui.classList.add('set-filter')
    this.gui.innerHTML = `
      <div class="set-filter-search">
        <input type="text" placeholder="Search..." class="set-filter-input" />
      </div>
      <div class="set-filter-actions">
        <a href="#" class="set-filter-link" data-action="all">Select All</a>
        <a href="#" class="set-filter-link" data-action="none">Deselect All</a>
      </div>
      <div class="set-filter-list"></div>
    `

    this.searchInput = this.gui.querySelector('.set-filter-input')
    this.listContainer = this.gui.querySelector('.set-filter-list')

    this.searchInput.addEventListener('input', () => this.renderList())

    this.gui.querySelector('[data-action="all"]').addEventListener('click', (e) => {
      e.preventDefault()
      this.allValues.forEach(v => this.selectedValues.add(v))
      this.renderList()
      this.params.filterChangedCallback()
    })

    this.gui.querySelector('[data-action="none"]').addEventListener('click', (e) => {
      e.preventDefault()
      this.selectedValues.clear()
      this.renderList()
      this.params.filterChangedCallback()
    })

    this.renderList()
  }

  afterGuiAttached() {
    // If values weren't passed in params, collect from grid data
    if (this.allValues.length === 0) {
      this.collectValues()
      this.allValues.forEach(v => this.selectedValues.add(v))
    }
    this.renderList()
    this.searchInput.focus()
  }

  collectValues() {
    const vals = new Set()
    this.params.api.forEachNode(node => {
      if (node.data) {
        const v = node.data[this.params.colDef.field]
        vals.add(v == null ? '(empty)' : String(v))
      }
    })
    this.allValues = [...vals].sort((a, b) => a.localeCompare(b))
  }

  renderList() {
    const search = (this.searchInput.value || '').toLowerCase()
    const filtered = this.allValues.filter(v => v.toLowerCase().includes(search))

    this.listContainer.innerHTML = ''
    filtered.forEach(value => {
      const label = document.createElement('label')
      label.classList.add('set-filter-item')

      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      checkbox.checked = this.selectedValues.has(value)
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) {
          this.selectedValues.add(value)
        } else {
          this.selectedValues.delete(value)
        }
        this.params.filterChangedCallback()
      })

      const span = document.createElement('span')
      span.style.display = 'inline-flex'
      span.style.alignItems = 'center'
      span.style.gap = '4px'
      if (this.params.cellRenderer) {
        span.innerHTML = this.params.cellRenderer(value)
      } else {
        span.textContent = value
      }

      label.appendChild(checkbox)
      label.appendChild(span)
      this.listContainer.appendChild(label)
    })
  }

  getGui() {
    return this.gui
  }

  doesFilterPass(params) {
    const value = params.data[this.params.colDef.field]
    const str = value == null ? '(empty)' : String(value)
    return this.selectedValues.has(str)
  }

  isFilterActive() {
    return this.selectedValues.size > 0 && this.selectedValues.size < this.allValues.length
  }

  getModel() {
    if (!this.isFilterActive()) return null
    return { values: [...this.selectedValues] }
  }

  setModel(model) {
    if (model && model.values) {
      this.selectedValues = new Set(model.values)
    } else {
      this.selectedValues = new Set()
      this.allValues.forEach(v => this.selectedValues.add(v))
    }
    this.renderList()
  }

  onNewRowsLoaded() {
    // Re-collect if params didn't have values
    if (!this.params.values) {
      this.collectValues()
    }
    this.selectedValues = new Set()
    this.allValues.forEach(v => this.selectedValues.add(v))
    this.renderList()
  }

  refresh() {
    this.renderList()
    return true
  }
}
